/* ================================================================
 * Copyright © 2025 Tobeunicorn Co., Ltd. All rights reserved.
 *
 * 프로젝트명 : ArmyLink V1.0
 * 설명 : 모집일정 다이얼로그
 * 버전 : 1.0
 * 파일명 : RecruitDialog.tsx
 * 작성자 : 강동형
 * 작성일 : 2025-01-19
 * ================================================================
 * 수정 : 2026-01-13, 강동형 : 축제 등록 UI 렌더링 조건추가
 * 수정 : 2026-02-05, 강동형 : UI 버그 수정
 *================================================================*/

import React from 'react'
import { Dialog, DialogContent, DialogTitle, Slide, Divider} from '@mui/material';
import { useRecruitStore } from 'src/stores/RecruitStore';
import {format} from 'date-fns'
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import RecruitSession from './RecruitSession';
import { TransitionProps } from '@mui/material/transitions';
import { Icon_Link } from '../common/CommonIcons';
import DialogFestivalRegister from './DialogFestivalRegister';
import styled from "styled-components";
import DialogFestival from './DialogFestival';

// 스타일영역
const TitleContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-weight: 600;
`;

// Transition 컴포넌트 정의
const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement<any, any>; },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} timeout={500} />;
});

const DetailTitleWrapper = styled.div`
    display: flex;
    border-radius: 4px;
    padding: 12px 0px;
    gap: 8px;
`;

const DetailWrapper = styled.div`
    border-radius: 4px;
`;

const DataWrapper = styled.div`
    display: flex;
    border: 1px solid #ededed;
    border-radius: 4px;
    padding: 12px 8px;
    gap: 8px;
`;

interface NowWrapperProps {
    now: string;
}

const NowWrapper = styled.div<NowWrapperProps>`
    font-size: 12px;
    margin: auto;
    border: 1px solid #0b4d3c;
    color: #0b4d3c;
    padding: 3px;
    min-width: 60px;
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 2px;

    background-color: ${({ now }) =>
        now === "추가과정"
        ? "#D1E9E7"
        : now === "입영일"
        ? "#A4E6D5"
        : now === "임관일"
        ? "#A4E6D5"
        : now === "접수일정"
        ? "#CEDBD8"
        : "#FFFFFF"};
`;

const TitleWrapper = styled.div`
    display: flex;
    justify-content: flex-start;
    align-items: center;
    flex: 1;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
`;

const ButtonWrapper = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 8px;
    margin-left: auto;
`;

const DetailContainer = styled.div`
    display: flex;
    justify-content: flex-start;
    align-items: center;
    gap: 20px;
`;

const DetailHeader = styled.div`
    min-width: 70px;
    max-width: 70px;
    font-weight: 600;
    font-size: 16px;
`;

const DetailBox = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 18px 11px;
`;

const DetailContent = styled.div`
    font-size: 14px;
    padding: 0px 5px;
`;

const HyperLink = styled.a`
    display: flex;
    justify-content: flex-start;
    align-items: center;
    gap: 5px;

    background-color: #eef6f8;
    width: 100%;
    font-size: 14px;
    text-decoration: underline;
    color: black;

    overflow: hidden; /* 넘치는 텍스트 숨김 */
`;

const EllipsisText = styled.span`
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    display: inline-block;
    flex-shrink: 1; /* flex 자식으로써 줄어들 수 있어야 함 */
`;

type Props = {
    tab :'모집 일정' | '축제 정보'
}

const RecruitDialog = ({tab}:Props) => {
    const isOpen = useRecruitStore((state)=>state.isOpen)
    const dialogCurrentDate = useRecruitStore((state)=>state.dialogCurrentDate)
    const dialogData = useRecruitStore((state)=>state.dialogData)
    const setField = useRecruitStore((state)=>state.setField)
    const selected = useRecruitStore((state)=>state.selected)
    const dialogState = useRecruitStore((state)=>state.dialogState)
    const reset = useRecruitStore((state)=>state.reset)

    const onClose = ()=> {
        setField('isOpen',false)
        setTimeout(()=>{
            reset('selected')
            reset('dialogData')
            setField('dialogState',tab)
        },300)
    }

    const handleBack = ()=>{
        if(dialogState === '축제 등록'){
            onClose()
        }
        if(dialogData.length === 0){
            onClose()
        }
        else{
            setField('selected',null)
        }
    }

  return (
    //보완필요 > 좀 더 자연스러운 트랜지션
    <Dialog
        open={isOpen}
        onClose={onClose}
        fullWidth
        TransitionComponent={Transition}
        PaperProps={{
            sx: {
                position: 'fixed',
                bottom: 0,
                margin: 0,
                width: '100vw',
                height: 'auto',
                borderTopLeftRadius: 16,
                borderTopRightRadius: 16,
                transition: 'height 0.5s ease-in-out'
            }
        }}
      >
        <TitleContainer>
            {dialogState === '축제 등록' ? 
                <DialogTitle sx={{fontSize:16, fontWeight:700, display:'flex',justifyContent:'center',alignItems:'center', gap:'8px',width:'100%'}}>
                    <KeyboardArrowLeft onClick={handleBack} style={{position:'absolute',left:'20px'}}/>
                    <span style={{flex:1, textAlign:'center'}}>
                        일정 등록
                    </span>
                </DialogTitle>
            :
                selected ? 
                    <DialogTitle sx={{fontSize:16, fontWeight:700, display:'flex',justifyContent:'center',alignItems:'center', gap:'8px'}}>
                        <KeyboardArrowLeft onClick={handleBack}/>
                        {selected.title}
                    </DialogTitle>
                :
                <DialogTitle sx={{fontSize:16}}>{dialogCurrentDate ? format(dialogCurrentDate, 'M월 d일') : ''}</DialogTitle>
            }
        </TitleContainer>
        <DialogContent sx={{display:'flex',flexDirection:'column',gap:'12px', paddingTop:'0px', paddingX:'20px'}}>
            {dialogState === '축제 등록' ? 
                <DialogFestivalRegister/>
            :
                selected ? 
                    <DetailWrapper>
                        <DetailTitleWrapper>
                        <NowWrapper now ={selected.course_type}>{selected.course_type === '접수일정' ? '접수':selected.course_type}</NowWrapper>
                            <TitleWrapper>{selected.title}</TitleWrapper>
                        </DetailTitleWrapper>
                        <RecruitSession sessionData={selected.sessionData} now={selected.course_name}/>
                        <Divider sx={{paddingTop:'18px'}}/>
                        <DetailBox>
                            <DetailContainer>
                                <DetailHeader>선발 유형</DetailHeader>
                                <DetailContent>{selected.recruitment_type}</DetailContent>
                            </DetailContainer>
                            <DetailContainer>
                                <DetailHeader>공고명</DetailHeader>
                                <DetailContent>{selected.title}</DetailContent>
                            </DetailContainer>
                            <DetailContainer>
                                <DetailHeader>지원 자격</DetailHeader>
                                <DetailContent>{selected.quilify}</DetailContent>
                            </DetailContainer>
                            <DetailContainer>
                                <DetailHeader>공고 URL</DetailHeader>
                                <HyperLink href={selected.urlLink} target="_blank" rel="noopener noreferrer">
                                    <Icon_Link style={{scale:0.65}}/> 
                                    <EllipsisText>{selected.urlLink}</EllipsisText>
                                </HyperLink>
                            </DetailContainer>
                        </DetailBox>
                    </DetailWrapper> 
                :
                    dialogState === '축제 정보' ? 
                        <DialogFestival dialogData={dialogData}/>
                    :
                        <>
                            {dialogData?.map((content:any,index:any)=>(
                                <DataWrapper onClick={()=>{setField('selected',content)}}>
                                    <NowWrapper now ={content.course_type}>{content.course_type === '접수일정' ? '접수':content.course_type}</NowWrapper>
                                    <TitleWrapper>{content.title}</TitleWrapper>
                                    <ButtonWrapper>
                                    <KeyboardArrowRight/>
                                    </ButtonWrapper>
                                </DataWrapper>
                            ))}
                        </>
            }
        </DialogContent>
    </Dialog>
    )
}

export default RecruitDialog